import { useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { Container, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import Navbar from "../Navbar/Navbar";
import UserSignedIn from "../Navbar/UserSignedIn";

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    padding: theme.spacing(3),
  },
  title: {
    paddingBottom: theme.spacing(2),
  },
  subtitle: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
}));

const TermsAndConditions = () => {
  const classes = useStyles();
  const { currentUser } = useAuth();

  // SEO Data
  const seoData = useMemo(
    () => ({
      title: "Terms and Conditions | GentleGourmet",
      description:
        "Read GentleGourmet's terms and conditions. Understand the rules, rights, and responsibilities that apply when you use our digestive-friendly recipe platform.",
      keywords:
        "terms and conditions, terms of service, user agreement, GentleGourmet terms, acceptable use",
      canonicalUrl: "https://gentlegourmet.app/pages/terms-and-conditions",
    }),
    []
  );

  // JSON-LD Structured Data
  const jsonLdWebPage = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: "Terms and Conditions - GentleGourmet",
      description: seoData.description,
      url: seoData.canonicalUrl,
      isPartOf: {
        "@type": "WebSite",
        name: "GentleGourmet",
        url: "https://gentlegourmet.app",
      },
    }),
    [seoData]
  );

  const jsonLdBreadcrumbs = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: "https://gentlegourmet.app/",
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Terms and Conditions",
          item: seoData.canonicalUrl,
        },
      ],
    }),
    [seoData]
  );

  return (
    <>
      {/* SEO Head Tags */}
      <Helmet>
        <html lang="en" />
        <title>{seoData.title}</title>
        <meta name="description" content={seoData.description} />
        <meta name="keywords" content={seoData.keywords} />
        <link rel="canonical" href={seoData.canonicalUrl} />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content={seoData.title} />
        <meta property="og:description" content={seoData.description} />
        <meta property="og:url" content={seoData.canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="GentleGourmet" />
        <meta property="og:locale" content="en_US" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={seoData.title} />
        <meta name="twitter:description" content={seoData.description} />
        <script type="application/ld+json">
          {JSON.stringify(jsonLdWebPage)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(jsonLdBreadcrumbs)}
        </script>
      </Helmet>
      {!currentUser ? <Navbar /> : <UserSignedIn />}
      <Container className={classes.root}>
        <Typography variant="h2" className={classes.title}>
          Terms and Conditions
        </Typography>
        <Typography>
          These Terms and Conditions govern your access to and use of
          GentleGourmet and the services we provide.
        </Typography>
        <Typography>
          By accessing or using the Service, you agree to be bound by these
          Terms. If you disagree with any part of the Terms, you may not access
          the Service.
        </Typography>

        <Typography variant="h4" className={classes.subtitle}>
          Accounts
        </Typography>
        <Typography>
          When you create an account with us, you must provide information that
          is accurate, complete, and current at all times. You are responsible
          for safeguarding the password you use to access the Service and for
          any activities or actions taken under your account. You agree to
          notify us immediately upon becoming aware of any breach of security or
          unauthorized use of your account.
        </Typography>

        <Typography variant="h4" className={classes.subtitle}>
          Acceptable Use
        </Typography>
        <Typography>
          You agree not to use the Service in any way that violates applicable
          law, infringes the rights of others, or interferes with the operation
          of the Service. This includes attempting to gain unauthorized access
          to any portion of the Service, scraping or harvesting data by
          automated means, and uploading content that is unlawful, harmful, or
          misleading.
        </Typography>

        <Typography variant="h4" className={classes.subtitle}>
          User Content
        </Typography>
        <Typography>
          You retain ownership of any recipes, reviews, images, or other content
          you submit to the Service. By submitting content, you grant us a
          non-exclusive, worldwide, royalty-free license to use, display,
          reproduce, and distribute that content in connection with operating
          and promoting the Service. You represent that you have the necessary
          rights to grant this license.
        </Typography>

        <Typography variant="h4" className={classes.subtitle}>
          Intellectual Property
        </Typography>
        <Typography>
          The Service and its original content, features, and functionality are
          and will remain the exclusive property of GentleGourmet and its
          licensors. Our trademarks and trade dress may not be used in
          connection with any product or service without our prior written
          consent.
        </Typography>

        <Typography variant="h4" className={classes.subtitle}>
          Health and Nutritional Disclaimer
        </Typography>
        <Typography>
          Recipes, ingredient guidance, and related information provided through
          the Service are offered for general informational purposes only and do
          not constitute medical, nutritional, or dietary advice. The Service is
          not a substitute for consultation with a qualified healthcare
          provider. Always seek the advice of your physician or another
          qualified health professional regarding any medical condition or
          dietary change, and check ingredient labels for allergens before
          preparing or consuming any dish.
        </Typography>

        <Typography variant="h4" className={classes.subtitle}>
          Subscriptions and Payments
        </Typography>
        <Typography>
          Certain portions of the Service may be offered on a subscription
          basis. Subscriptions are billed in advance on a recurring basis and
          renew automatically unless canceled before the end of the current
          billing period. You may cancel at any time through your account
          settings, and cancellation takes effect at the end of the period
          already paid for. Fees are non-refundable except where required by
          law.
        </Typography>

        <Typography variant="h4" className={classes.subtitle}>
          Third-Party Links and Services
        </Typography>
        <Typography>
          The Service may contain links to third-party websites or services that
          are not owned or controlled by GentleGourmet. We have no control over,
          and assume no responsibility for, the content, privacy policies, or
          practices of any third-party sites or services.
        </Typography>

        <Typography variant="h4" className={classes.subtitle}>
          Termination
        </Typography>
        <Typography>
          We may suspend or terminate your access to the Service immediately,
          without prior notice or liability, for any reason, including if you
          breach these Terms. Upon termination, your right to use the Service
          ceases immediately. Provisions that by their nature should survive
          termination will remain in effect.
        </Typography>

        <Typography variant="h4" className={classes.subtitle}>
          Limitation of Liability
        </Typography>
        <Typography>
          To the maximum extent permitted by law, GentleGourmet and its
          officers, employees, and affiliates shall not be liable for any
          indirect, incidental, special, consequential, or punitive damages
          arising out of your access to or use of, or inability to use, the
          Service.
        </Typography>

        <Typography variant="h4" className={classes.subtitle}>
          Disclaimer of Warranties
        </Typography>
        <Typography>
          The Service is provided on an "AS IS" and "AS AVAILABLE" basis without
          warranties of any kind, whether express or implied. We do not warrant
          that the Service will be uninterrupted, secure, or free of errors, or
          that any content is accurate or complete.
        </Typography>

        <Typography variant="h4" className={classes.subtitle}>
          Governing Law
        </Typography>
        <Typography>
          These Terms shall be governed and construed in accordance with the
          laws of the State of Oklahoma, United States, without regard to its
          conflict of law provisions.
        </Typography>

        <Typography variant="h4" className={classes.subtitle}>
          Changes to These Terms
        </Typography>
        <Typography>
          We may update these Terms from time to time. We will notify you of any
          changes by posting the new Terms on this page. You are advised to
          review these Terms periodically for any changes. Changes to these
          Terms are effective when they are posted on this page.
        </Typography>

        <Typography variant="h4" className={classes.subtitle}>
          Contact Us
        </Typography>
        <Typography>
          If you have any questions about these Terms, please contact us:
          <Link reloadDocument to="/pages/contact">
            Contact Us
          </Link>
        </Typography>
      </Container>
    </>
  );
};

export default TermsAndConditions;